export type EntityType = "items" | "orders";
export type FieldType = "text" | "number" | "integer" | "date";

export interface FieldDef {
    key: string;
    label: string;
    type: FieldType;
    required: boolean;
    // Alternative header names used for auto-mapping
    aliases: string[];
}

export interface EntityDef {
    type: EntityType;
    label: string;
    keyField: string;
    fields: FieldDef[];
    sample: string;
}

export type ImportedRecord = Record<string, string | number>;

export const ENTITIES: Record<EntityType, EntityDef> = {
    items: {
        type: "items",
        label: "Items",
        keyField: "sku",
        fields: [
            { key: "sku", label: "SKU", type: "text", required: true, aliases: ["code", "item code", "item_code", "product code", "article"] },
            { key: "name", label: "Name", type: "text", required: true, aliases: ["title", "description", "product", "item name"] },
            { key: "category", label: "Category", type: "text", required: false, aliases: ["group", "type"] },
            { key: "price", label: "Price", type: "number", required: true, aliases: ["unit price", "unit_price", "cost", "amount"] },
            { key: "stock", label: "Stock", type: "integer", required: false, aliases: ["qty", "quantity", "inventory", "on hand"] },
        ],
        sample: [
            "Item Code,Title,Category,Unit Price,Qty",
            "SKU-001,Wireless Headphones,Audio,89.99,25",
            "SKU-002,Mechanical Keyboard,Accessories,129.00,12",
            "SKU-003,Ergonomic Mouse,Accessories,49.50,40",
            "SKU-004,4K Monitor 27\",Displays,349.00,8",
            "SKU-005,Smart Watch,Wearables,abc,15",
            ",USB-C Hub,Accessories,39.00,30",
            "SKU-007,\"Laptop Stand, Aluminium\",Accessories,34.99,18",
        ].join("\n"),
    },
    orders: {
        type: "orders",
        label: "Orders",
        keyField: "orderId",
        fields: [
            { key: "orderId", label: "Order ID", type: "text", required: true, aliases: ["order", "order no", "order_no", "order number", "id"] },
            { key: "date", label: "Date", type: "date", required: true, aliases: ["order date", "order_date", "created"] },
            { key: "customer", label: "Customer", type: "text", required: true, aliases: ["client", "customer name", "buyer"] },
            { key: "sku", label: "Item SKU", type: "text", required: true, aliases: ["item", "item code", "product code", "code"] },
            { key: "quantity", label: "Quantity", type: "integer", required: true, aliases: ["qty", "amount", "units"] },
        ],
        sample: [
            "Order No;Order Date;Client;Item Code;Qty",
            "ORD-1001;2026-10-01;Acme Corp;SKU-001;2",
            "ORD-1002;2026-10-02;Globex;SKU-003;5",
            "ORD-1003;2026-10-03;Initech;SKU-004;1",
            "ORD-1004;2026-13-45;Umbrella Ltd;SKU-002;3",
            "ORD-1005;2026-10-05;Stark Industries;SKU-999;4",
            "ORD-1006;2026-10-06;Wayne Enterprises;SKU-007;-1",
        ].join("\n"),
    },
};

const normalize = (value: string) => value.toLowerCase().replace(/[^a-z0-9]/g, "");

// Map each target field to the index of the best matching source column (-1 if none)
export function autoMap(entity: EntityDef, headers: string[]): Record<string, number> {
    const mapping: Record<string, number> = {};
    const normalizedHeaders = headers.map(normalize);
    entity.fields.forEach(field => {
        const names = [field.key, field.label].concat(field.aliases).map(normalize);
        mapping[field.key] = normalizedHeaders.findIndex(h => names.indexOf(h) !== -1);
    });
    return mapping;
}

export interface RowResult {
    rowNumber: number;
    record: ImportedRecord;
    errors: string[];
    warnings: string[];
}

export function validateRows(
    entity: EntityDef,
    rows: string[][],
    mapping: Record<string, number>,
    existingItems: ImportedRecord[]
): RowResult[] {
    const seenKeys: Record<string, number> = {};
    const knownSkus: Record<string, boolean> = {};
    existingItems.forEach(item => {
        knownSkus[String(item.sku)] = true;
    });

    return rows.map((row, index) => {
        const record: ImportedRecord = {};
        const errors: string[] = [];
        const warnings: string[] = [];

        entity.fields.forEach(field => {
            const column = mapping[field.key];
            const raw = column >= 0 ? (row[column] || "").trim() : "";

            if (raw === "") {
                if (field.required) {
                    errors.push(`${field.label} is required`);
                }
                return;
            }

            if (field.type === "number" || field.type === "integer") {
                const num = Number(raw.replace(",", "."));
                if (isNaN(num)) {
                    errors.push(`${field.label} "${raw}" is not a number`);
                } else if (field.type === "integer" && !Number.isInteger(num)) {
                    errors.push(`${field.label} must be a whole number`);
                } else if (num < 0 || (field.key === "quantity" && num === 0)) {
                    errors.push(`${field.label} must be greater than ${field.key === "quantity" ? "0" : "or equal to 0"}`);
                } else {
                    record[field.key] = num;
                }
            } else if (field.type === "date") {
                const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(raw);
                // Round-trip through Date to reject impossible dates such as 2026-02-31
                const date = match && new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
                const isValid = !!match && !!date && date.getMonth() === Number(match[2]) - 1 && date.getDate() === Number(match[3]);
                if (!isValid) {
                    errors.push(`${field.label} "${raw}" is not a valid date (YYYY-MM-DD)`);
                } else {
                    record[field.key] = raw;
                }
            } else {
                record[field.key] = raw;
            }
        });

        const key = record[entity.keyField];
        if (key !== undefined) {
            const keyText = String(key);
            if (seenKeys[keyText] !== undefined) {
                errors.push(`Duplicate ${entity.keyField} (also on row ${seenKeys[keyText]})`);
            } else {
                seenKeys[keyText] = index + 2;
            }
        }

        if (entity.type === "orders" && record.sku !== undefined && !knownSkus[String(record.sku)]) {
            warnings.push(`SKU ${record.sku} not found in imported items`);
        }

        // +2: 1-based numbering plus the header line, so it matches the line in the file
        return { rowNumber: index + 2, record, errors, warnings };
    });
}
